import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface IAdminAccountStatementPdfRequest {
	/** The account whose latest statement is being downloaded. */
	accountId: string;
	/** Company name, used only to name the downloaded file. */
	companyName?: string;
}

/**
 * Downloads an account's latest billing statement from `GET account/{id}/statement/pdf`. The
 * endpoint answers with the raw PDF rather than an `OperationResult`, so the response is read as
 * a blob and handed to a synthetic anchor to trigger the browser download.
 *
 * The request is composed under the signed-in admin's own account impersonation prefix, matching
 * the legacy call — the API resolves the statement from the `{id}` path segment, not the prefix.
 *
 * Mutations have no MutationCache, so the error toast is wired here explicitly.
 */
export function useAdminAccountStatementPdfMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ accountId, companyName }: IAdminAccountStatementPdfRequest) =>
			new Promise<void>((resolve, reject) => {
				userSession.webServiceHelper
					.callWebServiceRawResponse(
						Api.ImpersonationBroker.composeApiUrl({
							impersonationContext: { account: { id: userSession.account.id } },
							urlPath: `account/${accountId}/statement/pdf`,
						}),
						'GET',
						null,
						async response => {
							const blob = await response.blob();
							const objectUrl = window.URL.createObjectURL(blob);
							const anchor = document.createElement('a');
							anchor.href = objectUrl;
							anchor.download = `Latest_Statement_${companyName ?? accountId}_${new Date().toISOString()}.pdf`;
							// Safari only honors a click on an anchor that is attached to the document.
							document.body.appendChild(anchor);
							anchor.click();
							anchor.remove();
							resolve();
						},
						reject
					)
					.catch(reject);
			}),
		onError: () => toast.error('Failed to download the last statement. Please try again.'),
	});
}
